import React from 'react';
import Image from 'next/image';
import type { TransformedPartner } from '@/types/partner';
import CloseButton from '../Elements/CloseButton';
import PartnerContent from '../Partners/PartnerContent';
import Button from '../Button/Button';
import { useUser } from '@clerk/nextjs';
import { T } from '@tolgee/react';
import { useHandleBooking } from '@/hooks/useHandleBooking';
import { BookingSource, BookingTypes } from '@/types/booking';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

type PartnerModalProps = {
  partner: TransformedPartner;
  isOpen: boolean;
  onClose: () => void;
  handleIsLimitReached: () => void;
};

const PartnerModal: React.FC<PartnerModalProps> = ({
  partner,
  isOpen,
  onClose,
  handleIsLimitReached,
}) => {
  const { user } = useUser();
  const locale = useSelectedLanguage();
  const { handleBooking, isLoading, isBookingSuccessful } = useHandleBooking();
  const handleBookingClick = async () => {
    const bookingParams = {
      externalId: partner?.external_id || '',
      userId: user?.id,
      type: BookingTypes.PARTNER,
      time: new Date(),
      courseId: partner?.id,
      partnerPromo: partner?.title,
      locale,
      source: BookingSource.USER,
    };

    const res = await handleBooking(bookingParams);

    if (res && 'exceedsLimit' in res) {
      handleIsLimitReached();
    }
  };

  if (!isOpen || !partner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className={`max-h-screen bg-white rounded-2xl shadow-lg w-full overflow-scroll ${isBookingSuccessful ? 'max-w-[500px]' : 'max-w-3xl'}`}
      >
        {/* Check if booking was successful */}
        {isBookingSuccessful ? (
          <div className="p-10 text-center">
            <h2 className="text-4xl font-medium tracking-tightest leading-tight mb-4">
              <T>apps_courses.booking_success.title.label</T>
            </h2>
            <p className="text-base font-poppins leading-normal font-medium mb-6">
              <T>apps_courses.booking_success.text.label</T>
            </p>
            <div className="w-32 m-auto">
              <Button onClick={onClose}>Weiter</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="relative h-72 rounded-2xl">
              <Image
                src={partner.imageUrl || '/img/partners/visual.jpg'}
                alt={partner.name}
                fill
                className="object-cover w-full h-52 rounded-2xl"
              />

              <PartnerContent logo={partner.logo} name={partner.name} title={partner.name} />
              <CloseButton className="absolute top-5 right-5" onClose={onClose} fill="black" />
            </div>
            <div className="">
              <div className="pt-8 px-12 relative">
                <div className="overflow-y-auto max-h-80">
                  <div className="text-base leading-normal tracking-tightest font-semibold">
                    <T>apps_courses.apps_courses_details.short_description.tag.label</T>
                  </div>
                  <p className="text-base leading-normal font-medium font-poppins mt-1.5 mb-5">
                    {partner.short_description}
                  </p>

                  <div className="text-base leading-normal tracking-tightest font-semibold">
                    <T>apps_courses.apps_courses_details.language.tag.label</T>
                  </div>
                  <p className="text-base leading-normal font-medium font-poppins mt-1.5 mb-5">
                    {partner.languages.join(', ')}
                  </p>

                  <div className="text-base leading-normal tracking-tightest font-semibold mb-3">
                    <T>apps_courses.apps_courses_details.features.tag.label</T>
                  </div>
                  <ul className="eap-bullet-list mt-1.5 mb-5">
                    {partner.features?.map((feature, index) => (
                      <li
                        key={index}
                        className="mb-3 text-base leading-normal font-medium font-poppins"
                      >
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="text-base leading-normal tracking-tightest font-semibold mb-1.5">
                    <T
                      keyName="apps_courses.apps_courses_details.description.tag.label"
                      params={{
                        partner_title: partner?.title || 'Unknown Partner',
                      }}
                      defaultValue={`Über ${partner?.title || 'Unknown Partner'}`}
                    />
                  </div>
                  <p className="text-base leading-normal font-medium font-poppins mt-1.5 mb-5">
                    {partner.description}
                  </p>
                </div>
                <span className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent pointer-events-none block"></span>
              </div>
              <div className="py-8 px-12">
                <Button
                  onClick={() => {
                    void handleBookingClick();
                  }}
                  variant={isLoading ? 'disabled' : 'primary'}
                >
                  <T>apps_courses.apps_courses_details.button.book.label</T>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerModal;
